/*
 * Inquiry form rules, shared by the form (browser) and /api/inquiry (server).
 */

import type { InquiryInput } from '@/types'

import { toPersianDigits } from '@/lib/format'

export type InquiryErrors = Partial<Record<keyof InquiryInput, string>>

export const INQUIRY_LIMITS: Record<keyof InquiryInput, number> = {
    fullName: 120,
    company: 160,
    country: 80,
    email: 200,
    phone: 40,
    productInterest: 160,
    quantity: 80,
    message: 4000,
    website: 200
}

export const EMPTY_INQUIRY: InquiryInput = {
    fullName: '',
    company: '',
    country: '',
    email: '',
    phone: '',
    productInterest: '',
    quantity: '',
    message: '',
    website: ''
}

const CARRIAGE_RETURN = String.fromCharCode(13)
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const singleLine = (value: unknown): string => {
    return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : ''
}

const multiLine = (value: unknown): string => {
    return typeof value === 'string' ? value.split(CARRIAGE_RETURN).join('').trim() : ''
}

export const normalizeInquiry = (raw: unknown): InquiryInput => {
    const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>

    return {
        fullName: singleLine(source.fullName),
        company: singleLine(source.company),
        country: singleLine(source.country),
        email: singleLine(source.email).replace(/\s/g, ''),
        phone: singleLine(source.phone),
        productInterest: singleLine(source.productInterest),
        quantity: singleLine(source.quantity),
        message: multiLine(source.message),
        website: singleLine(source.website)
    }
}

export const validateInquiry = (input: InquiryInput): InquiryErrors => {
    const errors: InquiryErrors = {}

    if (input.fullName.length < 2) errors.fullName = "لطفاً نام و نام خانوادگی خود را وارد کنید."

    if (!input.email) errors.email = "لطفاً نشانی ایمیل خود را وارد کنید."
    else if (!EMAIL_PATTERN.test(input.email)) errors.email = "نشانی ایمیل معتبر نیست، برای نمونه: name@company.com"

    if (input.message.length < 10) errors.message = "لطفاً درخواست خود را در یکی دو جمله بنویسید."

    for (const key of Object.keys(INQUIRY_LIMITS) as Array<keyof InquiryInput>) {
        if (!errors[key] && input[key].length > INQUIRY_LIMITS[key]) {
            errors[key] = `این بخش حداکثر ${toPersianDigits(INQUIRY_LIMITS[key])} نویسه می‌پذیرد.`
        }
    }

    return errors
}
